/**
 * Sticky Add-to-Cart Bar
 * Displays a persistent ATC bar on Product Detail Pages when users scroll past the original button
 * Supports A/B testing via Varify.io and comprehensive analytics tracking
 */

class StickyAddToCart {
  constructor() {
    // Generate unique instance ID for debugging
    this.instanceId = Math.random().toString(36).substr(2, 9);
    
    this.isProductPage = this.detectProductPage();
    
    // Get settings from multiple sources (Varify, theme config, fallback)
    this.isEnabled = this.getEnabledSetting();
    this.variant = this.getVariantSetting();
    
    if (!this.isProductPage || !this.isEnabled || this.variant === 'control') {
      return;
    }

    this.originalAtcButton = null;
    this.stickyBar = null;
    this.isVisible = false;
    this.productData = {};
    this.isAnimating = false; // Prevent overlapping animations
    this.lastVisibilityState = null; // Track last visibility state
    
    this.init();
  }

  detectProductPage() {
    // Multiple ways to detect product pages across different Shopify themes
    const checks = [
      // Body class patterns
      document.body.classList.contains('template-product'),
      document.body.classList.contains('page-product'),
      document.body.classList.contains('product-page'),
      
      // URL pattern
      /\/products\/[^\/]+(\?.*)?$/.test(window.location.pathname),
      
      // Product-specific elements
      !!document.querySelector('form[action="/cart/add"]'),
      !!document.querySelector('[data-product-json]'),
      !!document.querySelector('product-info'),
      !!document.querySelector('.product-form'),
      
      // Meta tags
      document.querySelector('meta[property="og:type"]')?.content === 'product'
    ];
    
    const isProductPage = checks.some(check => check);
    
    return isProductPage;
  }

  getEnabledSetting() {
    // If Varify.io is running a test with a non-control variant, use its settings
    if (window.stickyAtcSettings?.variant && window.stickyAtcSettings.variant !== 'control') {
      return window.stickyAtcSettings.enabled;
    }
    
    // For control variant or no A/B testing, use theme config
    if (window.stickyAtcConfig?.enabled !== undefined) {
      return window.stickyAtcConfig.enabled;
    }
    
    // Auto-enable: default to true if we have basic product data
    return !!document.querySelector('[data-product-json]');
  }

  getVariantSetting() {
    // If Varify.io is running a test with a non-control variant, use its variant
    if (window.stickyAtcSettings?.variant && window.stickyAtcSettings.variant !== 'control') {
      return window.stickyAtcSettings.variant;
    }
    
    // For control variant or no A/B testing, use image variant by default
    // This matches the "Show Product Image" setting from the theme
    return 'basic-image';
  }

  init() {
    this.findOriginalAtcButton();
    this.extractProductData();
    this.createStickyBar();
    this.setupScrollListener();
    this.setupEventListeners();
    this.setupResponsiveListeners();
    this.setupCleanupListeners();
    this.setupMobileViewportFix();
    
    // Check initial visibility state after all setup is complete
    this.checkInitialVisibility();
    
    this.trackEvent('sticky_atc_initialized', { variant: this.variant });
  }

  setupResponsiveListeners() {
    // Use matchMedia API for responsive behavior
    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    this.tabletMediaQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    this.desktopMediaQuery = window.matchMedia('(min-width: 1024px)');

    const handleMediaChange = () => {
      this.updateResponsiveState();
      this.updateBarContent();
    };

    this.mobileMediaQuery.addListener(handleMediaChange);
    this.tabletMediaQuery.addListener(handleMediaChange);
    this.desktopMediaQuery.addListener(handleMediaChange);

    // Initial update
    this.updateResponsiveState();
  }

  updateResponsiveState() {
    const isMobile = this.mobileMediaQuery.matches;
    const isTablet = this.tabletMediaQuery.matches;
    const isDesktop = this.desktopMediaQuery.matches;

    // Update bar attributes for CSS targeting
    if (this.stickyBar) {
      this.stickyBar.setAttribute('data-device', this.getDeviceType());
      
      // Hide/show based on device settings
      if (!this.shouldShowOnCurrentDevice()) {
        this.hide();
      }
    }
  }

  updateBarContent() {
    if (!this.stickyBar) return;

    // Regenerate content for current device
    const content = this.generateBarContent();
    this.stickyBar.innerHTML = content;
    
    // Re-bind events after content update
    this.setupEventListeners();
  }

  setupCleanupListeners() {
    // Cleanup when page is hidden or user navigates away
    const cleanup = () => this.cleanup();
    
    window.addEventListener('beforeunload', cleanup);
    window.addEventListener('pagehide', cleanup);
    
    // Note: Removed visibilitychange listener that was hiding the sticky bar 
    // when window loses focus. This was causing unintended UX issues.
  }

  cleanup() {
    // Remove scroll event listener
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }

    // Clear variant update timeout
    if (this.variantUpdateTimeout) {
      clearTimeout(this.variantUpdateTimeout);
      this.variantUpdateTimeout = null;
    }

    // Clear animation state
    this.isAnimating = false;
    this.lastVisibilityState = null;

    // Remove media query listeners
    if (this.mobileMediaQuery) {
      this.mobileMediaQuery.removeListener(this.updateResponsiveState);
    }
    if (this.tabletMediaQuery) {
      this.tabletMediaQuery.removeListener(this.updateResponsiveState);
    }
    if (this.desktopMediaQuery) {
      this.desktopMediaQuery.removeListener(this.updateResponsiveState);
    }

    // Remove sticky bar
    if (this.stickyBar) {
      this.stickyBar.remove();
    }

    // Remove debug indicator
    if (this.debugIndicator) {
      this.debugIndicator.remove();
    }

    // Cleanup mobile viewport fix
    if (this.cleanupViewportFix) {
      this.cleanupViewportFix();
    }

    // Remove any bottom padding that was added
    this.removeBottomPadding();

    
    // Clear global instance reference
    if (window.stickyAtcInstance === this) {
      window.stickyAtcInstance = null;
    }
  }

  /**
   * Setup mobile viewport fix for dynamic browser UI
   * Prevents layout shifts caused by mobile browser navigation bars
   */
  setupMobileViewportFix() {
    // Only apply on mobile devices
    if (!this.isMobile()) {
      return;
    }

    // Set initial CSS custom property for stable viewport height
    const setViewportHeight = () => {
      // Use window.innerHeight which updates in real-time
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--sticky-atc-vh', `${vh}px`);
    };

    // Set initial value
    setViewportHeight();

    // Debounced resize handler to update viewport height
    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setViewportHeight, 150);
    };

    // Listen for resize events (orientation change, keyboard show/hide)
    window.addEventListener('resize', debouncedResize);
    window.addEventListener('orientationchange', debouncedResize);

    // Cleanup function
    this.cleanupViewportFix = () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', debouncedResize);
      clearTimeout(resizeTimer);
    };
  }

  checkInitialVisibility() {
    // Allow a brief moment for DOM to settle
    setTimeout(() => {
      if (!this.originalAtcButton || !this.stickyBar) return;

      // Perform initial visibility check
      this.checkVisibility();
    }, 100); // Small delay to ensure DOM is ready
  }

  findOriginalAtcButton() {
    // Specific selectors for main product ATC button (excluding bundles/related products)
    const selectors = [
      // Target main product form specifically
      'product-form .product-form__submit',
      '.product-form__submit.button--primary',
      '#ProductSubmitButton-template',
      '[data-type="add-to-cart-form"] button[type="submit"]:not(.buttonToggle):not(.quick-add__submit)',
      '.product-form button[type="submit"][name="add"]:not(.buttonToggle)',
      // Fallback selectors
      'button[name="add"]:not(.buttonToggle):not(.quick-add__submit)',
      '.product-form__cart-submit'
    ];

    for (const selector of selectors) {
      // Use querySelectorAll to check multiple matches and pick the main one
      const buttons = document.querySelectorAll(selector);
      
      if (buttons.length === 1) {
        this.originalAtcButton = buttons[0];
        break;
      } else if (buttons.length > 1) {
        // If multiple buttons, pick the one that's NOT a bundle/quick-add button
        for (const button of buttons) {
          if (!button.classList.contains('buttonToggle') && 
              !button.classList.contains('quick-add__submit') &&
              !button.id.includes('originalbundle') &&
              !button.closest('.quick-add')) {
            this.originalAtcButton = button;
            break;
          }
        }
        if (this.originalAtcButton) break;
      }
    }
  }

  extractProductData() {
    // Initialize productData if not already set
    if (!this.productData) {
      this.productData = {};
    }

    // Extract product data from the theme's product JSON (preferred method)
    const productJson = document.querySelector('[data-product-json]');
    if (productJson) {
      try {
        const productJsonData = JSON.parse(productJson.textContent);
        this.productData = { ...this.productData, ...productJsonData };
        
        // The JSON should already have everything we need, but let's ensure image is set
        if (!this.productData.featured_image && this.productData.images && this.productData.images.length > 0) {
          this.productData.featured_image = this.productData.images[0];
        }
        
      } catch (e) {
        console.warn('Sticky ATC: Could not parse product JSON', e);
      }
    }

    // Fallback: extract from meta tags and page elements (only if JSON data missing)
    if (!this.productData.title) {
      this.productData.title = document.querySelector('.product__title, h1, [data-product-title]')?.textContent?.trim() || 'Product';
    }

    if (!this.productData.price) {
      const priceElement = document.querySelector('.price-item--regular, .price-item--sale, .price .price-item, [data-price]');
      this.productData.price = priceElement?.textContent?.trim() || '';
    }

    // Get current variant ID from form
    const variantInput = document.querySelector('input[name="id"], select[name="id"], [name="id"]');
    this.productData.currentVariantId = variantInput?.value || this.productData.variants?.[0]?.id;

    // Product rating support removed - no longer extracting rating data

    // Get variant-specific image (more accurate than featured_image)
    if (this.productData.variants && this.productData.currentVariantId) {
      const currentVariant = this.productData.variants.find(v => v.id == this.productData.currentVariantId);
      if (currentVariant && currentVariant.featured_image) {
        this.productData.current_variant_image = currentVariant.featured_image;
      }
    }

    // Fallback chain for images: variant image → featured_image → first image
    if (!this.productData.current_variant_image) {
      this.productData.current_variant_image = this.productData.featured_image || 
                                              (this.productData.images && this.productData.images[0]) || 
                                              null;
    }

    // Extract variant information for variant selector
    this.extractVariantOptions();
  }

  extractVariantOptions() {
    // Check if product has multiple variants
    this.productData.hasMultipleVariants = this.productData.variants && this.productData.variants.length > 1;
    
    if (!this.productData.hasMultipleVariants) {
      return;
    }

    // Extract options and variants for dropdown
    this.productData.options = [];
    
    // Get the first variant to determine how many options there are
    const firstVariant = this.productData.variants[0];
    if (!firstVariant) {
      return;
    }

    // Extract option names from the main product form if available
    const optionElements = document.querySelectorAll('select[name^="options["], fieldset legend');
    
    const optionNames = [];
    
    optionElements.forEach(element => {
      if (element.tagName === 'LEGEND') {
        const legendText = element.textContent.trim();
        // Skip if it contains "sold out" or "unavailable" text
        if (legendText && !legendText.toLowerCase().includes('sold out') && !legendText.toLowerCase().includes('unavailable')) {
          optionNames.push(legendText);
        }
      } else if (element.tagName === 'SELECT') {
        const name = element.getAttribute('name');
        const match = name.match(/options\[(.+)\]/);
        if (match) {
          optionNames.push(match[1]);
        }
      }
    });

    // If we can't find option names from form, try to derive from variant data
    if (optionNames.length === 0) {
      // Try to get option names from product.options if available
      if (this.productData.options_with_values) {
        this.productData.options_with_values.forEach(option => {
          optionNames.push(option.name);
        });
      } 
      
      // Otherwise check if variants have option1, option2, option3 properties
      if (optionNames.length === 0) {
        if (firstVariant.option1) optionNames.push('Size');
        if (firstVariant.option2) optionNames.push('Color'); 
        if (firstVariant.option3) optionNames.push('Style');
      }
    }

    // Check if this is a single-option product where values are in variant titles
    const hasOptionProperties = this.productData.variants.some(variant => 
      variant.option1 || variant.option2 || variant.option3
    );
    
    if (!hasOptionProperties && this.productData.variants.length > 1) {
      // Single option product - use variant titles as option values
      const values = [...new Set(
        this.productData.variants
          .map(variant => variant.title)
          .filter(title => title && title.trim() && title !== 'Default Title')
      )];
      
      if (values.length > 1) {
        // Use the first option name we found, or fallback to 'Size'
        const optionName = optionNames.length > 0 ? optionNames[0] : 'Size';
        this.productData.options.push({
          name: optionName,
          values: values,
          position: 1
        });
      }
    } else {
      // Multi-option product - use option1/option2/option3 properties
      
      optionNames.forEach((optionName, index) => {
        const optionIndex = index + 1;
        const optionKey = `option${optionIndex}`;
        
        // Get values from variants, filtering out empty/invalid ones
        let values = [...new Set(
          this.productData.variants
            .map(variant => {
              const value = variant[optionKey];
              return value;
            })
            .filter(value => value && value.trim() && value !== 'Default Title')
        )];
        
        // If no values found in option1/option2/option3, try alternative approaches
        if (values.length === 0 && this.productData.options_with_values) {
          const productOption = this.productData.options_with_values[index];
          if (productOption && productOption.values) {
            values = productOption.values.map(v => v.toString());
          }
        }
        
        if (values.length > 1) { // Only add if there are multiple values
          this.productData.options.push({
            name: optionName,
            values: values,
            position: optionIndex
          });
        }
      });
    }
  }

  createStickyBar() {
    this.stickyBar = document.createElement('div');
    this.stickyBar.className = 'sticky-atc-bar sticky-atc-bar--hidden';
    this.stickyBar.setAttribute('data-variant', this.variant);
    this.stickyBar.setAttribute('aria-hidden', 'true');
    
    const content = this.generateBarContent();
    this.stickyBar.innerHTML = content;
    
    document.body.appendChild(this.stickyBar);
  }

  generateBarContent() {
    const isMobile = window.innerWidth < 768;
    const config = window.stickyAtcConfig?.settings || {};
    const showImage = config.showImage && !isMobile && this.productData.current_variant_image;
    
    let content = '<div class="sticky-atc-content">';
    
    // Product image (desktop variants only) - using variant-specific image
    if (showImage) {
      // Ensure proper image sizing like the theme does (width: 300 for sticky context)
      let imageUrl = this.productData.current_variant_image;
      
      // Add width parameter if not already present (following theme approach)
      if (imageUrl && !imageUrl.includes('width=')) {
        imageUrl = imageUrl.includes('?') 
          ? `${imageUrl}&width=300` 
          : `${imageUrl}?width=300`;
      }
      
      content += `<div class="sticky-atc-image">
        <img src="${imageUrl}" alt="${this.productData.title}" loading="lazy" width="60" height="60">
      </div>`;
    }
    
    // Product info
    content += '<div class="sticky-atc-info">';
    
    // Product title (truncated for mobile)
    const title = isMobile ? this.truncateTitle(this.productData.title, 25) : this.productData.title;
    content += `<h3 class="sticky-atc-title">${title}</h3>`;
    
    // Price
    if (this.productData.price) {
      const formattedPrice = this.formatPrice(this.productData.price);
      content += `<div class="sticky-atc-price">${formattedPrice}</div>`;
    }
    
    // Rating support removed
    
    content += '</div>';

    // Variant selector (only if product has multiple variants)
    if (this.productData.hasMultipleVariants) {
      content += this.generateVariantSelector();
    }
    
    // Add to Cart button - matching default button structure with preorder support
    const isPreorder = this.productData.tags && this.productData.tags.includes('pre-order');
    const buttonText = isPreorder ? 'Pre-Order Now' : 'Add to cart';
    const buttonClass = `sticky-atc-btn product-form__submit button button--full-width button--primary${isPreorder ? ' button--preorder' : ''}`;
    
    content += `<div class="sticky-atc-actions">
      <button type="submit" name="add" class="${buttonClass}" data-variant-id="${this.productData.currentVariantId}" aria-haspopup="dialog"${isPreorder ? ' data-preorder="true"' : ''}>
        <span>${buttonText}</span>
        <div class="loading-overlay__spinner hidden">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      </button>
    </div>`;
    
    content += '</div>';
    
    return content;
  }

  generateVariantSelector() {
    if (!this.productData.options || this.productData.options.length === 0) {
      return '';
    }

    let selectorContent = '<div class="sticky-atc-variants" role="group" aria-label="Product options">';
    
    this.productData.options.forEach((option, optionIndex) => {
      selectorContent += `
        <div class="sticky-atc-variant-option">
          <label class="sticky-atc-variant-label" for="sticky-option-${optionIndex}">
            ${option.name}
          </label>
          <div class="sticky-atc-select-wrapper">
            <select 
              id="sticky-option-${optionIndex}" 
              class="sticky-atc-variant-select" 
              data-option-position="${option.position}"
              name="options[${option.name}]"
              aria-describedby="sticky-option-${optionIndex}-description"
              aria-required="true"
            >`;
      
      // Get current selected value from main form
      const currentSelected = this.getCurrentSelectedOption(option.position);
      
      option.values.forEach(value => {
        const isSelected = value === currentSelected ? 'selected' : '';
        selectorContent += `<option value="${value}" ${isSelected}>${value}</option>`;
      });
      
      selectorContent += `
            </select>
            <svg class="sticky-atc-select-icon" aria-hidden="true" viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.5 0.5L5 5L9.5 0.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div id="sticky-option-${optionIndex}-description" class="sr-only">
            Select ${option.name} for this product
          </div>
        </div>`;
    });
    
    selectorContent += '</div>';
    return selectorContent;
  }

  getCurrentSelectedOption(position) {
    // Try to get from current variant
    if (this.productData.currentVariantId && this.productData.variants) {
      const currentVariant = this.productData.variants.find(v => v.id == this.productData.currentVariantId);
      if (currentVariant) {
        return currentVariant[`option${position}`];
      }
    }

    // Fallback: get from main product form
    const mainFormSelect = document.querySelector(`select[name="options[${this.productData.options[position - 1]?.name}]"]`);
    if (mainFormSelect) {
      return mainFormSelect.value;
    }

    // Last fallback: first option value
    return this.productData.options[position - 1]?.values[0];
  }

  truncateTitle(title, maxLength) {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength).trim() + '...';
  }

  // generateStarRating method removed - rating support discontinued

  setupScrollListener() {
    if (!this.originalAtcButton) return;
    
    // Check if there's CSS sticky positioning enabled on the page
    const stickyElement = document.querySelector('.product__column-sticky');
    
    // Use throttle instead of no throttling to reduce interference with CSS sticky
    const handleScroll = this.throttle(() => {
      this.checkVisibility();
    }, 16); // ~60fps throttle to be smooth but less intensive

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Store reference for cleanup
    this.scrollHandler = handleScroll;
  }

  // Add throttle method if it doesn't exist
  throttle(func, wait) {
    let timeout;
    let lastExecTime = 0;
    return function executedFunction(...args) {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > wait) {
        func(...args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func(...args);
          lastExecTime = Date.now();
        }, wait - (currentTime - lastExecTime));
      }
    };
  }

  checkVisibility() {
    if (!this.originalAtcButton || !this.stickyBar) return;

    // Skip if currently animating to prevent overlapping animations
    if (this.isAnimating) {
      return;
    }

    // Check if the original button is above the viewport (user scrolled past it)
    const buttonRect = this.originalAtcButton.getBoundingClientRect();
    const isButtonAboveViewport = buttonRect.bottom < 0;
    
    // Skip if state hasn't changed to prevent unnecessary calls
    if (this.lastVisibilityState === isButtonAboveViewport) {
      return;
    }
    
    // Update last state
    this.lastVisibilityState = isButtonAboveViewport;
    

    
    // Show/hide logic
    if (isButtonAboveViewport && !this.isVisible) {
      this.show();
    } else if (!isButtonAboveViewport && this.isVisible) {
      this.hide();
    }
  }

  show() {
    // Early check - only block if already visible or currently animating to visible
    if (this.isVisible || this.isAnimating) {
      return;
    }
    
    // Set state immediately to prevent duplicate calls
    this.isAnimating = true;
    this.isVisible = true;
    
    // Remove hidden class and add visible class
    this.stickyBar.classList.remove('sticky-atc-bar--hidden');
    this.stickyBar.classList.add('sticky-atc-bar--entering');
    this.stickyBar.classList.add('sticky-atc-bar--visible');
    this.stickyBar.setAttribute('aria-hidden', 'false');
    
    // Add bottom padding to prevent footer overlap
    this.addBottomPadding();
    
    const animationDuration = this.getAnimationDuration();
    
    // Remove entering class after animation and clear animation state
    setTimeout(() => {
      this.stickyBar.classList.remove('sticky-atc-bar--entering');
      this.isAnimating = false;
    }, animationDuration);
    
    this.trackEvent('sticky_atc_shown', {
      variant: this.variant,
      scroll_position: window.scrollY,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      device_type: this.getDeviceType()
    });

    // Focus management for accessibility
    this.manageFocus();
  }

  hide() {
    // Early check - only block if already hidden or currently animating to hidden
    if (!this.isVisible || this.isAnimating) {
      return;
    }
    
    // Set state immediately to prevent duplicate calls
    this.isAnimating = true;
    this.isVisible = false;
    
    // Add leaving animation class
    this.stickyBar.classList.add('sticky-atc-bar--leaving');
    this.stickyBar.setAttribute('aria-hidden', 'true');
    
    // Remove bottom padding when hiding
    this.removeBottomPadding();
    
    const animationDuration = this.getAnimationDuration();
    
    // Remove visible class and add hidden class after animation starts
    setTimeout(() => {
      this.stickyBar.classList.remove('sticky-atc-bar--visible');
      this.stickyBar.classList.remove('sticky-atc-bar--leaving');
      this.stickyBar.classList.add('sticky-atc-bar--hidden');
      this.isAnimating = false;
    }, animationDuration / 2);
  }

  shouldShowOnCurrentDevice() {
    // Always show on all devices - device-specific settings removed
    return true;
  }

  getAnimationDuration() {
    return 300; // Hardcoded to 300ms
  }

  getDeviceType() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  isMobile() {
    return window.innerWidth < 768;
  }

  /**
   * Add bottom padding to page content to prevent footer overlap
   */
  addBottomPadding() {
    if (!this.stickyBar) return;
    
    // Calculate the actual height of the sticky bar including safe areas
    const stickyBarHeight = this.stickyBar.offsetHeight;
    const safeAreaBottom = parseInt(getComputedStyle(document.documentElement)
      .getPropertyValue('--sticky-atc-vh').replace('px', '')) || 0;
    
    // Use the larger of bar height or minimum touch-friendly space
    const paddingHeight = Math.max(stickyBarHeight, 70);
    
    // Find the main content wrapper - try common selectors
    const contentWrappers = [
      'main',
      '#MainContent', 
      '.main-content',
      '.page-content',
      '.shopify-section:last-of-type',
      'body'
    ];
    
    let targetElement = null;
    for (const selector of contentWrappers) {
      targetElement = document.querySelector(selector);
      if (targetElement) break;
    }
    
    if (targetElement) {
      // Store original padding for restoration
      if (!targetElement.dataset.originalPaddingBottom) {
        const currentPadding = getComputedStyle(targetElement).paddingBottom;
        targetElement.dataset.originalPaddingBottom = currentPadding;
      }
      
      // Apply new padding
      targetElement.style.paddingBottom = `${paddingHeight}px`;
      
      // Add a class for CSS targeting if needed
      document.body.classList.add('sticky-atc-active');
    }
  }

  /**
   * Remove bottom padding when sticky bar is hidden
   */
  removeBottomPadding() {
    // Find the same content wrapper
    const contentWrappers = [
      'main',
      '#MainContent', 
      '.main-content',
      '.page-content',
      '.shopify-section:last-of-type',
      'body'
    ];
    
    let targetElement = null;
    for (const selector of contentWrappers) {
      targetElement = document.querySelector(selector);
      if (targetElement && targetElement.dataset.originalPaddingBottom !== undefined) break;
    }
    
    if (targetElement && targetElement.dataset.originalPaddingBottom !== undefined) {
      // Restore original padding
      targetElement.style.paddingBottom = targetElement.dataset.originalPaddingBottom;
      delete targetElement.dataset.originalPaddingBottom;
      
      // Remove the class
      document.body.classList.remove('sticky-atc-active');
    }
  }

  manageFocus() {
    // Announce to screen readers when sticky bar appears
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = 'Add to cart option now available at bottom of screen';
    
    document.body.appendChild(announcement);
    
    // Remove announcement after screen reader has time to read it
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  setupEventListeners() {
    if (!this.stickyBar) return;

    const atcButton = this.stickyBar.querySelector('.sticky-atc-btn');
    if (atcButton) {
      atcButton.addEventListener('click', (e) => this.handleAddToCart(e));
    }

    // Add variant selector event listeners with debounced updates
    const variantSelects = this.stickyBar.querySelectorAll('.sticky-atc-variant-select');
    variantSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        // Immediate variant update for responsiveness
        this.updateSelectedVariant();
        
        // Debounced updates for performance
        if (this.variantUpdateTimeout) {
          clearTimeout(this.variantUpdateTimeout);
        }
        
        this.variantUpdateTimeout = setTimeout(() => {
          this.handleVariantChange(e);
        }, 150); // 150ms debounce
      });

      // Add keyboard navigation improvements
      select.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          // Let default behavior handle the selection
          setTimeout(() => {
            this.updateSelectedVariant();
            this.handleVariantChange(e);
          }, 10);
        }
      });
    });
  }

  handleVariantChange(event) {
    // Update selected variant based on current option selections
    this.updateSelectedVariant();
    
    // Update the button's data-variant-id attribute
    const button = this.stickyBar.querySelector('.sticky-atc-btn');
    if (button && this.productData.currentVariantId) {
      button.setAttribute('data-variant-id', this.productData.currentVariantId);
    }

    // Update the main product form to stay in sync
    this.syncWithMainForm();

    // Update price and availability
    this.updateProductInfo();

    // Announce changes to screen readers
    if (this.productData.currentVariant) {
      const announcement = `Selected ${this.productData.currentVariant.title}. Price: ${this.formatPrice(this.productData.currentVariant.price)}`;
      this.announceToScreenReader(announcement);
    }

    // Track variant change for analytics
    this.trackEvent('sticky_atc_variant_changed', {
      variant_id: this.productData.currentVariantId,
      variant_title: this.productData.currentVariant?.title,
      variant_available: this.productData.currentVariant?.available,
      device_type: this.getDeviceType()
    });
  }

  updateSelectedVariant() {
    if (!this.productData.hasMultipleVariants || !this.productData.variants) {
      return;
    }

    // Get current selected options from sticky selectors
    const selectedOptions = {};
    const selectedValues = [];
    const variantSelects = this.stickyBar.querySelectorAll('.sticky-atc-variant-select');
    
    variantSelects.forEach(select => {
      const position = select.dataset.optionPosition;
      selectedOptions[`option${position}`] = select.value;
      selectedValues.push(select.value);
    });



    // Check if this is a single-option product where values are in variant titles
    const hasOptionProperties = this.productData.variants.some(variant => 
      variant.option1 || variant.option2 || variant.option3
    );

    let matchingVariant;
    
    if (!hasOptionProperties && selectedValues.length === 1) {
      // Single option product - match by variant title
      matchingVariant = this.productData.variants.find(variant => 
        variant.title === selectedValues[0]
      );
    } else {
      // Multi-option product - match by option1/option2/option3
      matchingVariant = this.productData.variants.find(variant => {
        return Object.keys(selectedOptions).every(key => 
          variant[key] === selectedOptions[key]
        );
      });
    }

    if (matchingVariant) {
      this.productData.currentVariantId = matchingVariant.id;
      this.productData.currentVariant = matchingVariant;
    }
  }

  syncWithMainForm() {
    // Update the main product form variant selectors to stay in sync
    if (!this.productData.currentVariant) return;

    const mainVariantInput = document.querySelector('input[name="id"]');
    if (mainVariantInput) {
      mainVariantInput.value = this.productData.currentVariantId;
      mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update main form option selectors
    this.productData.options?.forEach(option => {
      // Try multiple selector patterns to find the main form select
      let mainSelect = document.querySelector(`select[name="options[${option.name}]"]`);
      if (!mainSelect) {
        // Try alternative selector patterns
        mainSelect = document.querySelector(`select[data-option-name="${option.name}"]`);
      }
      if (!mainSelect) {
        // Try by option position/index
        mainSelect = document.querySelector(`select[name="options[Option${option.position}]"]`);
      }
      
      const stickySelect = this.stickyBar.querySelector(`select[data-option-position="${option.position}"]`);
      
      if (mainSelect && stickySelect && mainSelect.value !== stickySelect.value) {
        mainSelect.value = stickySelect.value;
        mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  updateProductInfo() {
    if (!this.productData.currentVariant) return;

    // Update image
    if (this.productData.currentVariant.featured_image) {
      this.productData.current_variant_image = this.productData.currentVariant.featured_image;
      
      const imageElement = this.stickyBar.querySelector('.sticky-atc-image img');
      if (imageElement) {
        let imageUrl = this.productData.current_variant_image;
        if (imageUrl && !imageUrl.includes('width=')) {
          imageUrl = imageUrl.includes('?') 
            ? `${imageUrl}&width=300` 
            : `${imageUrl}?width=300`;
        }
        imageElement.src = imageUrl;
      }
    }

    // Update price - format like Shopify does
    const priceElement = this.stickyBar.querySelector('.sticky-atc-price');
    if (priceElement && this.productData.currentVariant.price !== undefined) {
      const price = this.productData.currentVariant.price;
      const formattedPrice = this.formatPrice(price);
      priceElement.textContent = formattedPrice;
    }

    // Update button availability
    const button = this.stickyBar.querySelector('.sticky-atc-btn');
    const buttonText = button?.querySelector('span');
    
    if (button && buttonText) {
      if (this.productData.currentVariant.available) {
        button.removeAttribute('disabled');
        // Use same preorder logic as generateBarContent
        const isPreorder = this.productData.tags && this.productData.tags.includes('pre-order');
        buttonText.textContent = isPreorder ? 'Pre-Order Now' : 'Add to cart';
        button.classList.remove('sticky-atc-btn--unavailable');
      } else {
        button.setAttribute('disabled', 'disabled');
        buttonText.textContent = 'Sold out';
        button.classList.add('sticky-atc-btn--unavailable');
      }
    }
  }

  formatPrice(priceInCents) {
    // Handle both cents (number) and formatted string prices
    if (typeof priceInCents === 'string') {
      // If it's already formatted (like "$44.00"), check if it needs currency code
      const config = window.stickyAtcConfig?.currency;
      if (config?.code && !priceInCents.includes(config.code)) {
        const result = `${priceInCents} ${config.code}`;
        return result;
      }
      return priceInCents;
    }
    
    if (typeof priceInCents === 'number') {
      // Get currency configuration
      const config = window.stickyAtcConfig?.currency;
      const currencyCode = config?.code || 'USD';
      
      // Convert cents to dollars and format with currency code
      const price = priceInCents / 100;
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode
      }).format(price);
      
      // Ensure currency code is included (some locales might not include it)
      if (!formatted.includes(currencyCode)) {
        return `${formatted} ${currencyCode}`;
      }
      
      return formatted;
    }
    
    // Fallback
    const config = window.stickyAtcConfig?.currency;
    const currencyCode = config?.code || 'USD';
    return `$0.00 ${currencyCode}`;
  }

  async handleAddToCart(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    
    if (!variantId) {
      console.warn('Sticky ATC: No variant ID found');
      this.showErrorFeedback('No product variant selected');
      return;
    }

    this.setButtonLoading(button, true);
    
    this.trackEvent('sticky_atc_clicked', {
      variant: this.variant,
      variant_id: variantId,
      scroll_position: window.scrollY,
      device_type: this.getDeviceType(),
      timestamp: Date.now()
    });

    try {
      // Use the same cart integration approach as the theme's product form
      const cartData = await this.addToShopifyCartWithSections(variantId, 1);
      this.handleAddToCartSuccess(cartData);
    } catch (error) {
      this.handleAddToCartError(error);
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  async addToShopifyCartWithSections(variantId, quantity = 1) {
    // Use the same cart integration approach as the theme's product form
    const cart = typeof window.getCartElement === 'function' ? window.getCartElement() : (document.querySelector('cart-notification') || document.querySelector('cart-drawer'));
    

    
    // Create FormData like the theme's product form does
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    
    // Include sections to render - this is the key difference!
    if (cart && cart.getSectionsToRender) {
      const sections = cart.getSectionsToRender().map((section) => section.id);
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
      

    }

    // Get routes.cart_add_url the same way as the theme
    const cartAddUrl = window.routes?.cart_add_url || '/cart/add';
    


    // Use the same fetch config approach as the theme
    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: formData
    };

    const response = await fetch(cartAddUrl, config);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Failed to add to cart';
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.description || errorMessage;
      } catch (e) {
        // Use default error message if JSON parsing fails

      }
      
      throw new Error(errorMessage);
    }

    // Check content type before attempting JSON parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      if (window.stickyAtcConfig?.settings?.debugMode) {
        console.error('Sticky ATC: Expected JSON but received:', contentType);
        console.error('Sticky ATC: Response text (first 200 chars):', responseText.substring(0, 200));
      }
      throw new Error('Server returned HTML instead of JSON. Please try again.');
    }

    const result = await response.json();
    
    // Handle errors in response
    if (result.status) {
      throw new Error(result.description || result.message || 'Failed to add to cart');
    }
    

    
    // Render cart contents using the theme's built-in system
    if (cart && cart.renderContents && !result.status) {
      cart.renderContents(result);
      
      // Ensure cart is no longer marked as empty (critical for display)
      if (cart.classList.contains('is-empty')) {
        cart.classList.remove('is-empty');
      }
      

    } else if (!cart) {
      // Fallback to cart page if no cart drawer
      window.location = window.routes?.cart_url || '/cart';
      return result;
    }

    return result;
  }

  // Keep the old method as fallback for backwards compatibility
  async addToShopifyCart(variantId, quantity = 1) {
    const requestBody = {
      items: [{
        id: variantId,
        quantity: quantity
      }]
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Failed to add to cart';
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.description || errorMessage;
      } catch (e) {
        // Use default error message if JSON parsing fails
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    
    // Validate response structure
    if (!result || !result.items) {
      throw new Error('Invalid response from cart API');
    }

    return result;
  }

  async updateCartDisplay() {
    try {
      // Fetch updated cart state
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      // Update cart count if cart drawer/header exists
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cartData.item_count;
      });

      // Trigger cart update events for theme compatibility
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: cartData 
      }));

      // Update Shopify section if it exists
      if (typeof window.Shopify?.section !== 'undefined') {
        window.Shopify.section.load('cart-drawer');
      }

    } catch (error) {
      console.warn('Sticky ATC: Failed to update cart display', error);
    }
  }

  setButtonLoading(button, isLoading) {
    const text = button.querySelector('span:not(.loading-overlay__spinner *)');
    const spinner = button.querySelector('.loading-overlay__spinner');
    
    if (isLoading) {
      button.classList.add('loading');
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      if (spinner) {
        spinner.classList.remove('hidden');
      }
    } else {
      button.classList.remove('loading');
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }
  }

  handleAddToCartSuccess(result) {
    const addedItem = result.items?.[0] || result;
    
    this.trackEvent('sticky_atc_success', {
      variant: this.variant,
      variant_id: addedItem.variant_id || addedItem.id,
      quantity: addedItem.quantity || 1,
      price: addedItem.price,
      product_id: addedItem.product_id,
      device_type: this.getDeviceType()
    });

    // Cart drawer contents are now automatically updated via renderContents()
    // No need to manually update display - the theme handles this
    
    // Show success feedback
    this.showSuccessFeedback();

    // Optional: Auto-hide after success (for better UX)
    if (window.stickyAtcConfig?.settings?.hideAfterAdd) {
      setTimeout(() => this.hide(), 800); // Reduced from 2000ms to 800ms 
    }
  }

  handleAddToCartError(error) {
    console.error('Sticky ATC: Add to cart failed', error);
    
    this.trackEvent('sticky_atc_error', {
      variant: this.variant,
      error: error.message,
      device_type: this.getDeviceType(),
      timestamp: Date.now()
    });
    
    // Show error feedback with specific message
    this.showErrorFeedback(error.message);
  }

  showSuccessFeedback(message = 'Added!') {
    const button = this.stickyBar.querySelector('.sticky-atc-btn');
    if (!button) return;
    
    const textElement = button.querySelector('span:not(.loading-overlay__spinner *)');
    if (!textElement) return;
    
    const originalText = textElement.textContent;
    
    textElement.textContent = message;
    button.classList.add('sticky-atc-btn--success');
    
    // Announce success to screen readers
    this.announceToScreenReader(`${message} Product added to cart`);
    
    setTimeout(() => {
      textElement.textContent = originalText;
      button.classList.remove('sticky-atc-btn--success');
    }, 2000);
  }

  showErrorFeedback(message = 'Error') {
    const button = this.stickyBar.querySelector('.sticky-atc-btn');
    if (!button) return;
    
    const textElement = button.querySelector('span:not(.loading-overlay__spinner *)');
    if (!textElement) return;
    
    const originalText = textElement.textContent;
    
    // Truncate long error messages for button display
    const displayMessage = message.length > 15 ? 'Error' : message;
    textElement.textContent = displayMessage;
    button.classList.add('sticky-atc-btn--error');
    
    // Announce error to screen readers with full message
    this.announceToScreenReader(`Error adding to cart: ${message}`);
    
    setTimeout(() => {
      textElement.textContent = originalText;
      button.classList.remove('sticky-atc-btn--error');
    }, 3000); // Longer timeout for errors
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'assertive');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  }

  trackEvent(eventName, data = {}) {
    // Use Universal Tracking (optimized - no duplicates)
    if (window.GTMTracking) {
      if (eventName === 'sticky_atc_success') {
        // Enhanced e-commerce tracking for successful add to cart
        const productData = {
          variant_id: data.variant_id,
          product_title: this.productData.title,
          product_type: this.productData.type,
          variant_title: data.variant_title || this.productData.variant?.title,
          price: data.price,
          quantity: data.quantity || 1,
          device_type: data.device_type,
          variant: data.variant
        };
        window.GTMTracking.trackStickyATCPurchase(productData);
      } else {
        // Standard sticky ATC tracking
        window.GTMTracking.trackStickyATC(eventName, {
          ...data,
          product_id: this.productData.id,
          product_title: this.productData.title,
          product_type: this.productData.type
        });
      }
    }

    // Legacy custom event for backwards compatibility
    document.dispatchEvent(new CustomEvent('stickyAtc:track', {
      detail: { event: eventName, data }
    }));
  }
}

// Function to initialize sticky cart (reusable for cache scenarios)
function initializeStickyCart() {
  if (!window.stickyAtcInstance) {
    window.stickyAtcInstance = new StickyAddToCart();
  }
}

// Singleton instance management to prevent multiple instances
if (!window.stickyAtcInstance) {
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeStickyCart);
  } else {
    initializeStickyCart();
  }
}

// CRITICAL: Handle browser back/forward (bfcache) scenarios where JS doesn't re-execute
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    // Page was loaded from browser cache - JavaScript didn't re-execute
    // Force initialization of sticky cart
    setTimeout(() => {
      initializeStickyCart();
    }, 50);
      }
    });

// Additional safety net for mobile browsers
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    // Page became visible - check if sticky cart exists
    setTimeout(() => {
      if (!window.stickyAtcInstance) {
        initializeStickyCart();
  }
    }, 100);
  }
}); 